import React from "react";
import {
  FaCarCrash,
  FaCheckCircle,
  FaClipboardCheck,
  FaMapMarkerAlt,
  FaRoute,
  FaTools,
} from "react-icons/fa";

const workflow = [
  {
    title: "Request intake",
    detail:
      "The driver logs the issue, vehicle, location, and urgency before the job is assigned.",
    marker: "01",
    icon: FaCarCrash,
  },
  {
    title: "Provider assigned",
    detail:
      "Carmie routes the request to a garage, towing team, or roadside support path.",
    marker: "02",
    icon: FaRoute,
  },
  {
    title: "Inspect and approve",
    detail:
      "The provider checks the vehicle, shares the service note, and waits for approval.",
    marker: "03",
    icon: FaTools,
  },
  {
    title: "Close the record",
    detail:
      "Payment, completion notes, and review stay connected to the driver's vehicle history.",
    marker: "04",
    icon: FaClipboardCheck,
  },
];

export default function HowItWorks() {
  return (
    <div className="bg-[#111714] py-24 text-white">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 sm:px-8 lg:grid-cols-[0.76fr_1fr] lg:px-12">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <p className="mb-4 inline-flex rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#8FE85E]">
            Service workflow
          </p>
          <h2 className="max-w-xl text-4xl font-black leading-[1.02] sm:text-5xl">
            From roadside problem to closed service record.
          </h2>
          <p className="mt-5 max-w-lg text-lg leading-8 text-white/70">
            Carmie turns the search for help into a managed job with a visible
            handoff, approval point, and record.
          </p>

          <div className="mt-8 rounded-md border border-white/12 bg-white/[0.06] p-5">
            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#8FE85E] text-[#111714]">
                <FaMapMarkerAlt aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D6F7C6]">
                  Example job
                </p>
                <h3 className="text-xl font-black">Tyre failure near Ntinda</h3>
              </div>
            </div>

            <div className="mt-4 grid gap-3 text-sm text-white/74">
              <div className="flex items-center justify-between">
                <span>Assigned path</span>
                <strong className="text-white">Roadside support</strong>
              </div>
              <div className="flex items-center justify-between">
                <span>Driver decision</span>
                <strong className="text-white">Approve quote</strong>
              </div>
              <div className="flex items-center justify-between">
                <span>Record status</span>
                <strong className="text-[#8FE85E]">Ready to close</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-[27px] top-6 hidden h-[calc(100%-48px)] w-px bg-[#8FE85E]/35 sm:block" />
          <div className="grid gap-4">
            {workflow.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="relative grid gap-4 rounded-md border border-white/10 bg-[#17211B] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.2)] sm:grid-cols-[56px_minmax(0,1fr)_auto] sm:items-center"
                >
                  <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-md border border-[#8FE85E]/40 bg-[#111714] text-[#8FE85E]">
                    <Icon aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#F4C44D]">
                      Step {step.marker}
                    </p>
                    <h3 className="mt-1 text-2xl font-black">{step.title}</h3>
                    <p className="mt-2 max-w-2xl text-base leading-7 text-white/68">
                      {step.detail}
                    </p>
                  </div>
                  <div className="hidden text-right sm:block">
                    <span className="inline-flex rounded-full border border-white/14 px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-white/58">
                      {step.marker}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex items-start gap-3 rounded-md bg-[#F7F8EF] p-4 text-[#111714]">
            <FaCheckCircle className="mt-1 shrink-0 text-[#3D7E2C]" aria-hidden="true" />
            <p className="text-sm font-bold leading-6">
              Each Carmie job has a clear next state, so drivers and providers
              know what is pending, approved, and complete.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
